/** @format */

import { useDropzone } from "react-dropzone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Brand } from "@question-bank/shared";
import { getApiBaseUrl, getAssetUrl } from "../api/questions";
import { authFetch, SessionExpiredError } from "../api/session";

type Props = {
	label: string;
	brand: Brand;
	onImageUpload: (assetKey: string) => void;
	imageInputId: string;
	existingImageUrl?: string;
	existingAssetKey?: string;
	onImageRemove?: () => void;
};

type ImageType = {
	id: string;
	file?: File;
	name: string;
	preview: string;
	status: "idle" | "uploading" | "success" | "error";
	assetKey?: string;
};

type PresignedResponse = {
	data: {
		uploadUrl: string;
		assetKey: string;
	};
};

export const ImageUpload = ({
	label,
	brand,
	onImageUpload,
	imageInputId,
	existingImageUrl,
	existingAssetKey,
	onImageRemove,
}: Props) => {
	const { t } = useTranslation();

	const assetsUrl = `${getApiBaseUrl(brand)}/assets`;

	// Build the public URL for a stored asset key (e.g. `assets/<uuid>.png`).
	// Assets are served by the brand's CloudFront asset distribution, decoupled
	// from the API, so previews must point at the asset domain (not the API).
	const assetKeyToUrl = (assetKey: string) => getAssetUrl(brand, assetKey);

	const [file, setFile] = useState<ImageType | null>(() => {
		const preview = existingImageUrl ?? (existingAssetKey ? assetKeyToUrl(existingAssetKey) : null);
		if (!preview || !existingAssetKey) {
			return null;
		}
		return {
			id: crypto.randomUUID(),
			file: undefined,
			name: existingAssetKey,
			preview,
			status: "success",
			assetKey: existingAssetKey,
		};
	});

	// Keep the preview in sync if the stored asset key changes (e.g. when the
	// same widget is reused for a different question/part). The image is shown
	// directly via an <img> tag, which renders cross-origin assets without the
	// CORS restrictions that block a fetch of the public asset endpoint.
	useEffect(() => {
		if (existingImageUrl || !existingAssetKey) {
			return;
		}
		setFile((prev) =>
			prev?.assetKey === existingAssetKey
				? prev
				: {
						id: crypto.randomUUID(),
						file: undefined,
						name: existingAssetKey,
						preview: assetKeyToUrl(existingAssetKey),
						status: "success",
						assetKey: existingAssetKey,
					},
		);
		// Only react to a change of the stored asset key.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [existingAssetKey, existingImageUrl]);

	const handleUpload = async (file: ImageType) => {
		if (!file.file) {
			return;
		}

		setFile((prev) => (prev ? { ...prev, status: "uploading" } : null));

		try {
			const response = await authFetch(assetsUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					filename: file.file.name,
					contentType: file.file.type,
					size: file.file.size,
				}),
			});

			if (!response.ok) {
				throw new Error("Unable to get presigned URL");
			}

			const { data }: PresignedResponse = await response.json();

			const s3Response = await fetch(data.uploadUrl, {
				method: "PUT",
				headers: { "Content-Type": file.file.type },
				body: file.file,
			});
			if (!s3Response.ok) {
				throw new Error("Failed to upload image");
			}

			setFile((prev) => (prev ? { ...prev, status: "success", assetKey: data.assetKey } : null));
			onImageUpload(data.assetKey);
		} catch (error) {
			setFile((prev) => (prev ? { ...prev, status: "error" } : null));
			if (error instanceof SessionExpiredError) {
				setError(t("sessionExpired.message"));
			} else if (error instanceof Error) {
				if (error.message === "Unable to get presigned URL") {
					setError(t("imageUpload.errorPresignedUrl"));
				} else {
					setError(t("imageUpload.errorUploadFailed"));
				}
			}
		}
	};

	const [error, setError] = useState<string | null>(null);

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
			"image/svg+xml": [".svg"],
		},
		maxSize: 5 * 1024 * 1024,
		onDrop: async (acceptedFiles) => {
			if (acceptedFiles.length === 0) {
				return;
			}
			const file = acceptedFiles[0];
			const newFile = {
				id: crypto.randomUUID(),
				file,
				name: file.name,
				preview: URL.createObjectURL(file),
				status: "idle" as const,
			};
			setFile(newFile);
			setError(null);
			await handleUpload(newFile);
		},
		onDropRejected: (rejectedFiles) => {
			const errorCode = rejectedFiles[0].errors[0].code;
			if (errorCode === "file-too-large") {
				setError(t("imageUpload.errorFileTooLarge"));
			} else if (errorCode === "file-invalid-type") {
				setError(t("imageUpload.errorInvalidFileType"));
			}
		},
	});

	useEffect(() => {
		return () => {
			if (file?.preview?.startsWith("blob:")) {
				URL.revokeObjectURL(file.preview);
			}
		};
	}, [file]);

	const handleRemove = async () => {
		if (!file) {
			return;
		}

		// Only delete from S3 immediately when the asset was uploaded during this
		// editing session (an orphan not yet referenced by a saved question).
		// For an already-saved image (`file.file` is undefined), removal is
		// deferred to "Save Changes": we just clear the field locally so the
		// question keeps referencing the asset until the user actually saves, and
		// never points at a deleted object.
		const isUnsavedUpload = Boolean(file.file);
		if (file.assetKey && isUnsavedUpload) {
			const removeUrl = `${assetsUrl}/${file.assetKey}`;
			try {
				const remove = await authFetch(removeUrl, {
					method: "DELETE",
				});

				if (!remove.ok) {
					setError(t("imageUpload.errorRemoveFailed"));
					return;
				}
			} catch (error) {
				setError(
					error instanceof SessionExpiredError ? t("sessionExpired.message") : t("imageUpload.errorRemoveFailed"),
				);
				return;
			}
		}
		setFile(null);
		setError(null);
		onImageRemove?.();
	};

	return (
		<div className="image-container w-full">
			{error && <p className="text-red-600 text-md mb-2">{error}</p>}
			<div>
				<label className="text-md flex mb-2" htmlFor={imageInputId}>
					<b>{label}</b>
				</label>
			</div>
			<div className="w-32">
				{file ? (
					<div className="relative w-full aspect-square">
						{file.preview ? (
							<img
								alt="preview thumbnail"
								src={file.preview}
								className="w-full h-full object-cover rounded-sm"
								onError={() => setError(t("imageUpload.errorLoadFailed"))}
							/>
						) : (
							<div className="w-full h-full rounded-sm bg-gray-100" />
						)}
						{file.status === "uploading" ? (
							<div className="absolute inset-0 flex items-center justify-center bg-black/40">
								<output>
									<div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
								</output>
							</div>
						) : (
							<button
								className="absolute -top-2 -right-2 bg-red-600 h-6 w-6 rounded-full font-bold flex items-center justify-center text-white text-xs"
								onClick={handleRemove}
								aria-label={t("imageUpload.removeButton")}
							>
								X
							</button>
						)}
					</div>
				) : (
					<div
						{...getRootProps({
							className:
								"border border-dashed border-gray-400 rounded-sm w-full aspect-square flex items-center justify-center cursor-pointer",
						})}
					>
						<input {...getInputProps({ id: imageInputId })} />
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-10 w-10 text-gray-400"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
							/>
						</svg>
					</div>
				)}
			</div>
		</div>
	);
};
